// Polyfill and shim includes
import 'core-js/actual';
import 'airbnb-js-shims';
import 'eventsource-polyfill';
import './assets/helpers/polyfills/canvas-to-blob';
import './assets/helpers/polyfills/canvas2Blob';
import cssVars from 'css-vars-ponyfill';

import React, { useEffect, useContext } from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import store from './store/store';
import { BrowserRouter } from 'react-router-dom';
import { init, captureException } from '@sentry/react';
import { Integrations } from '@sentry/tracing';
import { SplitFactory, SplitContext, useTreatments } from '@splitsoftware/splitio-react';

import './index.css';
import './theme.css';
import App from './App';
import registerServiceWorker, { unregister } from './registerServiceWorker';
import config from './constants/config';
import { ErrorBoundary } from 'react-error-boundary';
import { ErrorFallback } from './components/UI/Alert/ErrorFallback'

cssVars({});

const ErrorBoundaryWrapper = ({ children }) => {
  // access status properties via the SplitContext
  const { isReady } = useContext(SplitContext);
  // evaluate feature flags with the `useTreatments` hook
  const treatments = useTreatments(config.FEATURE_FLAGS);
  const { treatment } = treatments['isSentryEnabledForWeb'];
  const isSentryEnabledForWeb = treatment === 'on';

  useEffect(() => {
    if (isReady && isSentryEnabledForWeb && config.SENTRY_DNS && config.SENTRY_RELEASE) {
      // inject sentry
      init({
        dsn: config.SENTRY_DNS,
        integrations: [new Integrations.BrowserTracing()],
        tracesSampleRate: config.ENVIRONMENT === 'production' ? 0.15 : 1.0,
        environment: config.ENVIRONMENT,
        release: `${config.SENTRY_RELEASE}@${config.VERSION}`,
        // whitelistUrls: [/bikers|widower\.dating/],
        // no localhost:3000 to reduce spam notifications
        blacklistUrls: [/taboola\.com|adsbackend\.com|localhost:3000|\.clarity\.ms|\.snapchat\.com/],
        // Fix for UnhandledRejection microsoft bots cause
        // https://sentry.io/organizations/nb-social/issues/2460614616/?project=5726384&query=is%3Aunresolved&statsPeriod=14d
        // https://forum.sentry.io/t/unhandledrejection-non-error-promise-rejection-captured-with-value/14062/16
        ignoreErrors: [
          // 'Non-Error exception captured',
          // 'Non-Error promise rejection captured',
          'Loading chunk 1 failed.',
          'Loading chunk 2 failed.',
          'Loading chunk 3 failed.',
          'Loading chunk 4 failed.',
          'Loading chunk 5 failed.',
          'Loading chunk 6 failed.',
          'Loading chunk 7 failed.',
          'Loading chunk 8 failed.',
          'Loading chunk 9 failed.',
          'Loading chunk 21 failed.',
          // "Can't find variable: _AutofillCallbackHandler",
          // "Identifier 'change_ua' has already been declared",
        ],
      });
    }
  }, [isReady, isSentryEnabledForWeb]);

  const myErrorHandler = (error, info) => {
    if (isReady && isSentryEnabledForWeb && config.SENTRY_DNS && config.SENTRY_RELEASE) {
      captureException(error, info.componentStack);
    }
    // eslint-disable-next-line no-console
    console.error('JSErrorBoundary !!!!:', error, info.componentStack);
  };

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback} onError={myErrorHandler}>
      {children}
    </ErrorBoundary>
  );
}

const SPLITIO_CONFIG = {
  core: {
    authorizationKey:
      config.ENVIRONMENT === 'production'
        ? config.SPLITIO_AUTH_KEY_PROD
        : config.SPLITIO_AUTH_KEY_DEV,
    key: config.SPLITIO_USER_KEY,
  }
};

const Application = () => (
  <SplitFactory config={SPLITIO_CONFIG}>
    <Provider store={store}>
      {/* <ErrorBoundaryWrapper> */}
      <BrowserRouter>
        <App />
      </BrowserRouter>
      {/* </ErrorBoundaryWrapper> */}
    </Provider>
  </SplitFactory>
);

ReactDOM.render(<Application />, document.getElementById('root'));

if (
  (config.ENVIRONMENT === 'production' || config.ENVIRONMENT === 'dev') &&
  config.ENABLE_SERVICE_WORKER === true
) {
  registerServiceWorker();
} else {
  unregister();
}
